import { FC, useEffect, useState } from 'react';
import { getPayoutHistories } from '@src/services/payout.ts';
import { TPayout } from '@src/types/payoutRequests.ts';
import { formatDate } from '@lib/utils.ts';

export const PayoutHistory: FC<{ userId?: number | null }> = ({ userId }) => {
  const [payouts, setPayouts] = useState<TPayout[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        // Use the histories endpoint. If `userId` is provided, pass it as a query param
        const res = await getPayoutHistories(userId ?? undefined);
        const data: any = res.data;

        // Backend may return either { payouts: [...] } or { accounts: [ { payouts: [...] } ] }
        if (Array.isArray(data?.accounts)) {
          setAccounts(data.accounts ?? []);
          const flat: TPayout[] = data.accounts.flatMap((a: any) =>
            (a.payouts ?? []).map((p: any) => p)
          );

          setPayouts(flat);
        } else if (Array.isArray(data?.payouts)) {
          // Build accounts array from payouts grouped by leeloo_account_id so we can render account headers
          const grouped: Record<string, any> = {};

          data.payouts.forEach((p: any) => {
            const key = String(
              p.leeloo_account_id ?? p.leeloo_account?.id ?? 'unknown'
            );

            if (!grouped[key])
              grouped[key] = {
                leeloo_account_id: p.leeloo_account_id,
                leeloo_account: p.leeloo_account,
                payouts: [],
              };
            grouped[key].payouts.push(p);
          });
          const accountsArr = Object.keys(grouped).map((k) => ({
            ...grouped[k],
            total_payout_amount: grouped[k].payouts.reduce(
              (s: number, it: any) =>
                s + Number(parseFloat(String(it.amount || 0)) || 0),
              0
            ),
          }));

          setAccounts(accountsArr);
          setPayouts(data.payouts ?? []);
        } else {
          setAccounts([]);
          setPayouts([]);
        }
      } catch (err: any) {
        console.error('Failed to fetch payout histories', err);
        setError(err?.message ?? 'Failed to load payout histories');
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [userId]);

  // We'll render using `accounts` when available (this matches backend response). If not, build accounts from payouts.
  const acctList = accounts.length
    ? accounts
    : (() => {
        const grouped = payouts.reduce(
          (acc: Record<string, any>, p) => {
            const key = String(
              p.leeloo_account_id ?? p.leeloo_account?.id ?? 'unknown'
            );

            if (!acc[key])
              acc[key] = {
                leeloo_account_id: p.leeloo_account_id,
                leeloo_account: p.leeloo_account,
                payouts: [],
              };
            acc[key].payouts.push(p);

            return acc;
          },
          {} as Record<string, any>
        );

        return Object.keys(grouped).map((k) => ({ ...grouped[k] }));
      })();

  const toggle = (key: string) =>
    setExpanded((s) => ({ ...s, [key]: !s[key] }));

  return (
    <div>
      <h2 className={'text-lg font-medium mb-4'}>Payout History</h2>
      {loading ? (
        <p>Loading payout history...</p>
      ) : error ? (
        <p className={'text-red-500'}>Error: {error}</p>
      ) : payouts.length === 0 ? (
        <p>No payout history found.</p>
      ) : (
        <div>
          {/* Summary line (matches screenshot style) */}
          <div className={'mb-4 text-sm text-muted-foreground'}>
            <p>
              Total paid out: $
              {payouts
                .filter((p) => p.paid_on)
                .reduce(
                  (s, it) =>
                    s + Number(parseFloat(String(it.amount || 0)) || 0),
                  0
                )}{' '}
              | Current payout ratio: 100%
            </p>
          </div>

          {/* header row (clone table header style) */}
          <div className={'w-full bg-muted/50 border-b border-border'}>
            <div
              className={
                'max-w-full mx-auto px-2 py-3 grid grid-cols-12 items-center space-x-3 text-sm'
              }
            >
              <div className={'col-span-4 font-bold'}>Account ID</div>
              <div className={'col-span-2 text-right font-bold'}>
                Last Payout
              </div>
              <div className={'col-span-1 text-right font-bold'}>
                Total Payouts
              </div>
              <div className={'col-span-1 text-right font-bold'}>
                Total Paid $
              </div>
              <div className={'col-span-2 text-right font-bold'}>
                Account Max
              </div>
              <div className={'col-span-2 text-right font-bold'}>
                Payout Ratio
              </div>
            </div>
          </div>

          {acctList.map((acct: any) => {
            const key = String(
              acct.leeloo_account_id ?? acct.leeloo_account?.id ?? 'unknown'
            );
            const account = acct.leeloo_account ?? acct.leeloo_account;
            const lastPayout =
              acct.last_payout ??
              (acct.payouts && acct.payouts.length
                ? acct.payouts
                    .slice()
                    .sort((a: any, b: any) =>
                      (
                        b.paid_on ||
                        b.approved_on ||
                        b.created_at
                      ).localeCompare(
                        a.paid_on || a.approved_on || a.created_at
                      )
                    )[0]?.paid_on
                : null);
            const paidCount =
              (acct.total_payout_paid ??
                (acct.payouts || []).filter((p: any) => !!p.paid_on).length) ||
              0;
            const pendingCount =
              acct.total_payout_pending ??
              (acct.payouts || []).filter((p: any) => !p.paid_on).length;
            const totalPaid =
              acct.total_payout_amount ??
              acct.total_payout_amount ??
              (acct.payouts || [])
                .filter((p: any) => !!p.paid_on)
                .reduce(
                  (s: number, it: any) =>
                    s + Number(parseFloat(String(it.amount || 0)) || 0),
                  0
                );

            return (
              <div key={key} className={'border-b border-border'}>
                <div
                  className={
                    'max-w-full mx-auto px-2 py-4 grid grid-cols-12 space-x-3 items-start gap-2 hover:bg-muted/50 transition-colors'
                  }
                >
                  <div className={'col-span-4 flex items-center gap-3'}>
                    <button
                      className={
                        'w-6 h-6 rounded-full border border-border bg-card text-muted-foreground flex items-center justify-center text-xs'
                      }
                      onClick={() => toggle(key)}
                    >
                      {expanded[key] ? '−' : '+'}
                    </button>
                    <div>
                      <div className={'font-medium text-foreground'}>
                        {account?.rithmic_account_name ?? `PA-${key}`}
                      </div>
                      <div className={'text-xs text-muted-foreground'}>
                        {account?.product?.title ?? ''}
                      </div>
                    </div>
                  </div>
                  <div className={'col-span-2 text-right'}>
                    {lastPayout ? formatDate(lastPayout) : '-'}
                  </div>
                  <div className={'col-span-1 text-right'}>
                    {paidCount} / {pendingCount}
                  </div>
                  <div className={'col-span-1 text-right'}>${totalPaid}</div>
                  <div className={'col-span-2 text-right'}>
                    ${acct.account_max ?? account?.account_balance ?? '-'}
                  </div>
                  <div className={'col-span-2 text-right'}>
                    {acct.payout_ratio ?? acct.payout_ratio ?? '100%'}
                  </div>
                </div>

                {expanded[key] ? (
                  <div className={'max-w-full mx-auto px-4 pb-6'}>
                    <div className={'overflow-x-auto'}>
                      <table
                        className={
                          'min-w-full text-sm border-collapse table-fixed'
                        }
                      >
                        <colgroup>
                          <col style={{ width: '6%' }} />
                          <col style={{ width: '14%' }} />
                          <col style={{ width: '12%' }} />
                          <col style={{ width: '16%' }} />
                          <col style={{ width: '12%' }} />
                          <col style={{ width: '18%' }} />
                          <col style={{ width: '22%' }} />
                        </colgroup>
                        <thead className={'bg-muted/50'}>
                          <tr>
                            <th className={'text-left px-4 py-2'}>ID</th>
                            <th className={'text-left px-4 py-2'}>Created</th>
                            <th className={'text-left px-4 py-2'}>Amount $</th>
                            <th className={'text-left px-4 py-2'}>
                              Rithmic Deduction $
                            </th>
                            <th className={'text-left px-4 py-2'}>Method</th>
                            <th className={'text-left px-4 py-2'}>Status</th>
                            <th className={'text-left px-4 py-2'}>Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(acct.payouts || []).map((item: any) => {
                            let status = '';
                            let statusHtml = '';

                            if (item.deleted_at) {
                              if (item.denied_on) {
                                statusHtml = `<span class="text-red-500">Denied</span><br/><small>${formatDate(item.denied_on)}</small>`;
                              } else {
                                status = 'Cancelled';
                              }
                            } else {
                              if (!item.completed_on) {
                                status = 'Awaiting customer to complete';
                              } else {
                                if (!item.approved_on) {
                                  statusHtml = `Awaiting staff approval<br/><small>Completed: ${formatDate(item.completed_on)}</small>`;
                                } else if (!item.paid_on) {
                                  statusHtml = `Awaiting payout<br/><small>Approved: ${formatDate(item.approved_on)}</small>`;
                                } else {
                                  statusHtml = `Paid<br/><small>${formatDate(item.paid_on)}</small>`;
                                }
                              }
                            }

                            return (
                              <tr
                                key={item.id}
                                className={'border-t border-border'}
                              >
                                <td className={'px-4 py-3'}>{item.id}</td>
                                <td className={'px-4 py-3'}>
                                  {formatDate(item.created_at)}
                                </td>
                                <td className={'px-4 py-3'}>${item.amount}</td>
                                <td className={'px-4 py-3'}>
                                  ${item.rithmic_deduction}
                                </td>
                                <td className={'px-4 py-3'}>
                                  {(item.payout_method || '').toUpperCase()}
                                </td>
                                <td className={'px-4 py-3'}>
                                  {statusHtml ? (
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: statusHtml,
                                      }}
                                      className={'font-medium'}
                                    />
                                  ) : (
                                    <div className={'font-medium'}>
                                      {status}
                                    </div>
                                  )}
                                </td>
                                <td className={'px-4 py-3'}>
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: item.note ?? '',
                                    }}
                                    className={'text-sm text-gray-700'}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PayoutHistory;
